/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useEffect, useRef, useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

// Material Dashboard 2 PRO React examples
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import FormField from "layouts/applications/wizard/components/FormField";
import { useNavigate } from "react-router-dom";

import CrudService from "services/cruds-service";
import MDEditor from "components/MDEditor";
import { Popover, List, ListItem, ListItemText } from "@mui/material";
import { MESSAGE_TEMPLATE_VARIABLES } from "utils/constant";
import Scrollbar from "react-perfect-scrollbar"; // Import Scrollbar component
import { useMaterialUIController } from "context";

const NewMessage = () => {
  const navigate = useNavigate();
  const [message, setMessage] = useState({
    name: "",
    content: "",
  });

  const [error, setError] = useState({
    name: "",
    content: "",
  });
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  const [anchorEl, setAnchorEl] = useState(null);
  const quillRef = useRef(null);
  const [cursorPosition, setCursorPosition] = useState(null);
  const [popoverPosition, setPopoverPosition] = useState({ top: 0, left: 0 });
  const variables = MESSAGE_TEMPLATE_VARIABLES;

  useEffect(() => {
    // Listen for keydown event to detect Shift + #
    const handleKeyDown = (e) => {
      if (e.shiftKey && e.key === "#") {
        const quill = quillRef.current?.getEditor();
        if (quill) {
          const selection = quill.getSelection();
          if (selection) {
            const cursorBounds = quill.getBounds(selection.index);
            setCursorPosition(selection.index);
            setPopoverPosition({
              top: cursorBounds.top, // Top position of the cursor
              left: cursorBounds.left, // Left position of the cursor
            });
            setAnchorEl(document.activeElement); // Show the popover
          }
        }
      }
    };

    // Add event listener
    window.addEventListener("keydown", handleKeyDown);

    // Cleanup event listener
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  const submitHandler = async (e) => {
    e.preventDefault();

    // const formattedCompany = company.map((element) => element.attributes);
    const newMessage = {
      data: {
        type: "messages",
        attributes: {
          name: message.name,
          content: message.content,
        },
      },
    };

    try {
      await CrudService.createMessage(newMessage);
      navigate("/message-management", {
        state: { value: true, text: "The Message was successfully created" },
      });
    } catch (err) {
      if (err.hasOwnProperty("errors")) {
        setError({ ...error, error: true, textError: err.errors[0].detail });
      }
      console.error(err);
    }
  };

  const changeNameHandler = (e) => {
    setMessage({ ...message, name: e.target.value });
  };

  const handleEditorChange = (value) => {
    if (value.includes("#")) {
      setAnchorEl(document.activeElement);
      return;
    }
    setMessage({ ...message, content: value });
  };

  const handleVariableInsertion = (variable) => {
    const quill = quillRef.current?.getEditor();
    if (quill && cursorPosition !== null) {
      quill.insertText(cursorPosition, `{{${variable}}}`);
      quill.setSelection(cursorPosition + `{{${variable}}}`.length);
    }
    setAnchorEl(null);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox mt={4} mb={5}>
        <Grid container justifyContent="center">
          <Grid item xs={12} lg={8}>
            <MDBox mt={0} mb={2} textAlign="center">
              <MDBox mb={1}>
                <MDTypography variant="h3" fontWeight="bold">
                  New Message
                </MDTypography>
              </MDBox>
              <MDTypography variant="h5" fontWeight="regular" color="secondary">
                This information will describe more about the Message.
              </MDTypography>
            </MDBox>
            <Card>
              <MDBox component="form" method="POST" onSubmit={submitHandler}>
                <MDBox display="flex" flexDirection="column" px={3} my={2}>
                  <FormField
                    type="text"
                    label=" Name"
                    name="name"
                    value={message.name}
                    onChange={changeNameHandler}
                    error={error.name}
                  />

                  <MDBox mt={2}>
                    <MDBox
                      mb={1}
                      ml={0.5}
                      lineHeight={0}
                      display="inline-block"
                    >
                      <MDTypography
                        component="label"
                        variant="button"
                        fontWeight="regular"
                        color="text"
                      >
                        Message Content&nbsp;&nbsp;
                      </MDTypography>
                    </MDBox>
                    <MDEditor
                      value={message.content}
                      onChange={handleEditorChange}
                      ref={quillRef}
                    />
                    <Popover
                      open={Boolean(anchorEl)}
                      anchorEl={anchorEl}
                      onClose={() => setAnchorEl(null)}
                      anchorOrigin={{
                        vertical: "bottom",
                        horizontal: "left",
                      }}
                      transformOrigin={{
                        vertical: "top",
                        horizontal: "left",
                      }}
                      style={{
                        position: "absolute",
                        top: popoverPosition.top + window.scrollY - 170,
                        left: popoverPosition.left + window.scrollX - 10,
                      }}
                    >
                      <Scrollbar>
                        <List
                          style={{
                            backgroundColor: !darkMode ? "white" : "black",
                            color: darkMode ? "white" : "black",
                            overflowY: "scroll",
                            maxHeight: "200px",
                          }}
                        >
                          {" "}
                          <MDTypography>
                            Message Template Variables
                          </MDTypography>
                          {variables.map((variable, index) => (
                            <ListItem
                              key={index}
                              button
                              onClick={() => handleVariableInsertion(variable)}
                            >
                              <ListItemText>
                                <MDTypography variant="body2">
                                  * {variable}
                                </MDTypography>
                              </ListItemText>
                            </ListItem>
                          ))}
                        </List>
                      </Scrollbar>
                    </Popover>
                  </MDBox>

                  <MDBox
                    ml="auto"
                    mt={4}
                    mb={2}
                    display="flex"
                    justifyContent="flex-end"
                  >
                    <MDBox mx={2}>
                      <MDButton
                        variant="gradient"
                        color="dark"
                        size="small"
                        px={2}
                        mx={2}
                        onClick={() =>
                          navigate("/message-management", {
                            state: { value: false, text: "" },
                          })
                        }
                      >
                        Back
                      </MDButton>
                    </MDBox>
                    <MDButton
                      variant="gradient"
                      color="dark"
                      size="small"
                      type="submit"
                    >
                      Save
                    </MDButton>
                  </MDBox>
                </MDBox>
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
};

export default NewMessage;
